import { DefaultFile } from '@components/file-field/types';
import type { JsonValue } from '@components/json-editor';
import {
  FieldMappingEditorValue,
  FieldType,
  VariableValue,
} from '@components/text-editors/addons/field-mapping';
import { PickLiteral } from '@utils/types';

export type ConditionComparator =
  | 'eq'
  | 'neq'
  | 'contains'
  | 'ncontains'
  | 'starts_with'
  | 'ends_with'
  | 'empty'
  | 'nempty'
  | 'boolean_true'
  | 'boolean_false'
  | 'changes_value'
  | 'date_lt'
  | 'date_lte'
  | 'date_gt'
  | 'date_gte'
  | 'date_eq'
  | 'date_neq'
  | 'date_empty'
  | 'date_nempty'
  | 'number_lt'
  | 'number_lte'
  | 'number_gt'
  | 'number_gte'
  | 'number_eq'
  | 'number_neq'
  | 'number_starts_with'
  | 'number_ends_with'
  | 'number_empty'
  | 'number_nempty'
  | 'select_eq'
  | 'select_neq'
  | 'select_any_of'
  | 'select_none_of'
  | 'select_empty'
  | 'select_nempty'
  | 'select_multi_eq'
  | 'select_multi_neq'
  | 'select_multi_any_of'
  | 'select_multi_none_of'
  | 'select_multi_empty'
  | 'select_multi_nempty'
  | 'file_contains'
  | 'file_ncontains'
  | 'file_starts_with'
  | 'file_ends_with'
  | 'file_empty'
  | 'file_nempty';

type CommonIntegrationEndpointVariable<T extends string> = {
  name: string;
  type: T;
  label: string;
  template?: IntegrationEndpointVariable[];
  visible?: boolean;
  jqQuery?: string;
};

export type IntegrationEndpointUpdateType = {
  variables?: IntegrationEndpointVariable[];
  exampleData?: string;
  responseValues?: IntegrationEndpoint['responseValues'];
};

export type IntegrationTestRequestType = {
  success: boolean;
  statusCode: number;
  data: object;
};

export type WebhookCondition = {
  id: string;
  field?: VariableValue;
  comparator?: ConditionComparator;
  value?: string | FieldMappingEditorValue;
  visible?: boolean;
};

export type IntegrationWebhook = {
  shortId: string;
  name: string;
  method: EndpointMethod;
  url: string;
  uniqueUrl: string;
  webhookCreated: boolean;
  requestData: object;
  variables: IntegrationEndpointVariable[];
  payloadVariables: IntegrationEndpointVariable[];
  responseValues: IntegrationEndpointVariable[];
  integration: string;
  exampleData: JsonValue;
  payloadData: JsonValue;
  conditions: WebhookCondition[];
  isSubscribable: boolean;
  thumbnail?: string;
};

export type AuthenticationTestType = {
  authType: AuthenticationType;
  details: Authentication['details'];
};

export type BaseVariableExample = string | null;
export type FileVariableExample = string | File | null;
export type ArrayVariableExample = null | object[];
export type BooleanVariableExample = boolean | null;

type IntegrationEndpointTypes = PickLiteral<
  FieldType,
  | 'email'
  | 'text'
  | 'number'
  | 'date'
  | 'paragraph'
  | 'decimal'
  | 'array_of_strings'
  | 'webhookUrl'
  | 'phone'
>;

export type IntegrationEndpointBaseVariable =
  CommonIntegrationEndpointVariable<IntegrationEndpointTypes> & {
    example?: BaseVariableExample;
    default_value?: BaseVariableExample;
  };

export type IntegrationEndpointDateVariable = CommonIntegrationEndpointVariable<'date'> & {
  example?: BaseVariableExample;
  default_value?: BaseVariableExample;
  format?: string;
};

export type IntegrationEndpointSelectVariable = CommonIntegrationEndpointVariable<'select'> & {
  example?: string;
  default_value?: string;
  options_from?: string;
  options: {
    value: string;
    label: string;
  }[];
};

type IntegrationEndpointFileVariable = CommonIntegrationEndpointVariable<'file'> & {
  example?: FileVariableExample;
  default_value?: FileVariableExample;
};

type IntegrationEndpointArrayVariable = CommonIntegrationEndpointVariable<'array'> & {
  example?: ArrayVariableExample;
  default_value?: ArrayVariableExample;
  output_format?: 'object' | 'array';
};

type IntegrationEndpointBooleanVariable = CommonIntegrationEndpointVariable<'boolean'> & {
  example?: BooleanVariableExample;
  default_value?: BooleanVariableExample;
};

type BaseAuthentication = {
  shortId?: string;
  extraHeaders?: object;
  isAuthenticated: boolean;
};

export type OAuth2Authentication = BaseAuthentication & {
  authType: 'oauth2';
  details: {
    tokenUrl: string;
    clientId: string;
    clientSecret: string;
    grantType: string;
  };
};

export type BasicAuthentication = BaseAuthentication & {
  authType: 'basic';
  details: {
    username: string;
    password: string;
  };
};

export type BearerTokenAuthentication = BaseAuthentication & {
  authType: 'bearer_token';
  details: {
    clientId: string;
    clientSecret: string;
    url: string;
    tokenLocation: string;
    extraData: object;
    clientIdKey?: string;
    clientSecretKey?: string;
    prefix?: string;
    requestType?: 'json' | 'multipart';
  };
};

export type AuthorizationHeaderAuthentication = BaseAuthentication & {
  authType: 'authorization_header';
  details: {
    prefix: string;
    token: string;
  };
};

export type ApiKeyAuthentication = BaseAuthentication & {
  authType: 'api_key';
  details: {
    apiKey: string;
    headerKey: string;
  };
};

export type JwtTokenAuthentication = BaseAuthentication & {
  authType: 'jwt_token';
  details: {
    issuer: string;
    key: string;
    secret: string;
    expirationSeconds?: number;
    subject?: string;
    prefix?: string;
  };
};

export type NoAuthentication = BaseAuthentication & {
  authType: '';
  details: {};
};

export type IntegrationListOptions = {
  queryString?: string;
  isTested?: boolean;
};

export type AuthenticationType = Authentication['authType'];

export type EmptyFieldHandling = 'empty_string' | 'null' | 'skip';

export type IntegrationEndpointVariable =
  | IntegrationEndpointBaseVariable
  | IntegrationEndpointSelectVariable
  | IntegrationEndpointFileVariable
  | IntegrationEndpointBooleanVariable
  | IntegrationEndpointArrayVariable;

export type IntegrationWebhookCreateType = Omit<
  IntegrationWebhook,
  | 'shortId'
  | 'uniqueUrl'
  | 'webhookCreated'
  | 'requestData'
  | 'variables'
  | 'responseValues'
  | 'conditions'
  | 'thumbnail'
> & {
  integration: string;
  exampleData: string;
  payloadData: string;
  isSubscribable: boolean;
};

export type Authentication = {
  authType:
    | 'oauth2'
    | 'basic'
    | 'bearer_token'
    | 'authorization_header'
    | 'api_key'
    | 'jwt_token'
    | '';
  details: any;
  shortId?: string;
  extraHeaders?: object;
  isAuthenticated: boolean;
};

export type IntegrationBase = {
  name?: string;
  description?: string;
  logo?: File | null;
  emptyFieldHandling: EmptyFieldHandling;
  baseUrl?: string;
};

export type Integration = {
  shortId: string;
  name: string;
  description?: string;
  logo?: string;
  thumbnail?: string;
  authentication: Authentication | null;
  endpoints: Array<IntegrationEndpoint<string>>;
  webhooks: Array<IntegrationWebhook>;
  numJourneys: number;
  emptyFieldHandling: EmptyFieldHandling;
  baseUrl?: string;
};

export type DuplicateIntegrationData = {
  name: string;
};

export type DuplicateWebhookData = {
  name: string;
};

export const endpointMethods = ['POST', 'GET', 'PATCH', 'PUT', 'DELETE'] as const;
export type EndpointMethod = (typeof endpointMethods)[number];

export const requestTypes = ['json', 'multipart', 'graphql', 'file'] as const;
export type RequestTypes = (typeof requestTypes)[number];

export type IntegrationEndpoint<T extends Integration | string = Integration> = {
  shortId: string;
  name: string;
  method: EndpointMethod;
  requestType: (typeof requestTypes)[number];
  url: string;
  variables: IntegrationEndpointVariable[];
  responseValues: IntegrationEndpointVariable[];
  integration: T;
  numJourneys: number;
  exampleData: JsonValue;
  exampleResponseData?: JsonValue;
  graphqlRequest: string;
  schemaUrl?: string;
  exampleFiles?: ExampleFileType[];
  defaultFiles?: DefaultFileType[];
};

type ExampleFileType = DefaultFile & {
  variableName: string;
};

export type DefaultFileType = DefaultFile & {
  variableName: string;
};

export type ValidateJqQueryParams = {
  query: string;
  exampleData: object;
};

export type ValidateJQQueryResponse = {
  success: boolean;
  result?: any;
  error?: string;
};

export type IntegrationEndpointDetail = IntegrationEndpoint & { thumbnail?: string };

export type IntegrationEndpointMinimal = {
  shortId: string;
  name: string;
  thumbnail?: string;
};

export type IntegrationWebhookMinimal = {
  shortId: string;
  name: string;
  thumbnail?: string;
};

export type IntegrationTravellerActionFile = {
  file?: string;
};

export type ListSyncTestRequestType = Omit<IntegrationTestRequestType, 'data'> & {
  exampleData: object;
  arrayLocation: string;
  mappingKeys: string[];
  error?: string;
};

export type IntegrationWebhookCreateRequestType = {
  success: boolean;
  statusCode: number;
  data: object;
};

export type EditPollingVisibilityType = {
  visibleIds: string[];
};

export type DuplicateEndpointData = {
  name: string;
};

export type IntegrationWebhookLog = {
  dateCreated: string;
  shortId: string;
  requestData: object;
};

export type IntegrationWebhookUpdateType = {
  variables?: IntegrationEndpointVariable[];
  payloadVariables?: IntegrationEndpointVariable[];
  exampleData?: string;
  payloadData?: string;
  conditions?: WebhookCondition[];
};

export type IntegrationWebhookCreateRemoveType = {
  url: string;
  method: EndpointMethod;
  body: string;
};

export type IntegrationEndpointCreateType = IntegrationEndpoint<string> & {
  integration: Integration['shortId'];
  exampleData: string;
};

export type GenerateJqQueryParams = {
  prompt: string;
  exampleData: object;
};

export type GenerateJQQueryResponse = {
  success: boolean;
  query?: string;
  result?: any;
  suggestedType?: string;
  error?: string;
};

export type IntegrationEndpointPollingType = Pick<
  IntegrationEndpoint<string>,
  'shortId' | 'name' | 'method' | 'url' | 'variables'
> & {
  showInPolling: boolean;
  isValidPollingEndpoint: boolean;
  isUsedInJourney: boolean;
};

export type IntegrationPollingType = Pick<
  Integration,
  'shortId' | 'name' | 'description' | 'logo' | 'thumbnail'
> & {
  endpoints: IntegrationEndpointPollingType[];
};

export type IntegrationApiRequests = {
  loadIntegration: (shortId: string) => Promise<Integration>;
  loadIntegrations: (queryString: string, isTested: boolean) => Promise<Integration[]>;
  createIntegration: (data: IntegrationBase) => Promise<Integration>;
  deleteIntegration: (shortId: string) => Promise<void>;
  loadIntegrationEndpoint: (shortId: string) => Promise<IntegrationEndpointDetail>;
  loadIntegrationWebhook: (shortId: string) => Promise<IntegrationWebhook>;
  loadIntegrationWebhookLogs: (
    shortId: string
  ) => Promise<Array<{ dateCreated: string; shortId: string; requestData: object }>>;

  createIntegrationEndpoint: (data: any) => Promise<IntegrationEndpoint>;
  updateIntegrationEndpoint: (shortId: string, data: any) => Promise<IntegrationEndpoint>;
  deleteIntegrationEndpoint: (shortId: string) => Promise<void>;
  updateIntegrationEndpointExampleFiles: (shortId: string, data: FormData) => Promise<void>;
  updateIntegrationEndpointDefaultFiles: (shortId: string, data: FormData) => Promise<void>;

  createIntegrationWebhook: (data: any) => Promise<IntegrationWebhook>;
  updateIntegrationWebhook: (shortId: string, data: any) => Promise<IntegrationWebhook>;
  deleteIntegrationWebhook: (
    shortId: string,
    data?: { url: string; method: string; body?: string }
  ) => Promise<void>;
  subscribeIntegrationWebhook: (shortId: string, data: object) => Promise<any>;
  unsubscribeIntegrationWebhook: (shortId: string, data: any) => Promise<any>;

  updateIntegrationAuthentication: (shortId: string, data: Authentication) => Promise<Integration>;
  updateIntegration: ({
    integrationId,
    data,
  }: {
    integrationId: Integration['shortId'];
    data: IntegrationBase;
  }) => Promise<any>;
  authenticationTest: (data: any) => Promise<any>;

  integrationTestRequest: (
    shortId: string,
    data: { variables: IntegrationEndpointVariable[] }
  ) => Promise<any>;

  duplicateIntegration: (shortId: string, data: DuplicateIntegrationData) => Promise<Integration>;
  duplicateIntegrationEndpoint: (
    shortId: string,
    data: { name: string }
  ) => Promise<IntegrationEndpoint>;
  duplicateIntegrationWebhook: (
    shortId: string,
    data: { name: string }
  ) => Promise<IntegrationWebhook>;

  validateJqQuery: (data: {
    query: string;
    exampleData: object;
  }) => Promise<ValidateJQQueryResponse>;
  generateJqQuery: (data: {
    prompt: string;
    exampleData: object;
  }) => Promise<GenerateJQQueryResponse>;

  loadLists: (queryString?: string) => Promise<Array<List>>;
};

type Option = {
  shortId: string;
  value: string;
  label: string;
  order?: number;
};

export type List = {
  id: string;
  shortId: string;
  name: string;
  source: string;
  numOptions: number;
  options: Option[];
  isHidden: boolean;
  listType: 'synced' | 'manual';
  dateCreated: Date;
  sync?: SyncListType;
};

export type SyncListType = {
  shortId: string;
  endpointUrl: string;
  mappingLabel: string;
  mappingValue: string;
  frequency: string;
  hasError: boolean;
  errorMessage: string;
  integration: Integration;
  isActive: boolean;
  lastSync: Date;
  mappingKeys?: string[];
};
